import "./styles.scss";
import BarMenu from "../BarMenu";
import UploadFile from "./UploadFile";
import { useState, useRef, useEffect, useCallback } from "react";
import { MdOutlineFileUpload } from "react-icons/md";
import ErrorPopup from "./ErrorPopup";
import api from "../../utils/api";

const checkListItems = (data) => {
    const requiredFields = ['awb', 'dataDoUltimoStatus', 'descricaoDoStatus'];

    for (let index = 0; index < data.length; index++) {
        const item = data[index];

        for (let field of requiredFields) {
            if (!Object.prototype.hasOwnProperty.call(item, field)) {
                const msg = `Campo "${field}" não encontrado no item ${index + 1}. Verifique os dados.`;
                console.error(msg, item);
                throw new Error(msg);
            }
        }
    }
};

export default function TicketUpdates() {
    const [errorMsg, setErrorMsg] = useState("");
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState({ current: 0, total: 0, percentage: 0, itemsProcessed: 0, totalItems: 0 });
    const [results, setResults] = useState(null);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [isCopying, setIsCopying] = useState(false);
    const uploadRef = useRef(null);

    const handleErrorClose = () => {
        setErrorMsg('');
        setResults(null);
        setMessage({ type: '', text: '' });
        if (uploadRef.current) {
            uploadRef.current.reset();
        }
    };

    const sendUpdatesToBackend = useCallback(async (rawData) => {
        if (!rawData || rawData.length === 0) return;

        setIsProcessing(true);
        setMessage({ type: 'info', text: '📤 Processando atualizações de tickets...' });

        try {
            const CHUNK_SIZE = 100;
            const chunks = [];

            for (let i = 0; i < rawData.length; i += CHUNK_SIZE) {
                chunks.push(rawData.slice(i, i + CHUNK_SIZE));
            }

            let totalSuccess = 0;
            let totalFailed = 0;
            let totalNotFound = 0;
            const allResults = {
                success: [],
                failed: [],
                notFound: []
            };

            for (let i = 0; i < chunks.length; i++) {
                const percentage = Math.round(((i + 1) / chunks.length) * 100);
                const itemsProcessed = Math.min((i + 1) * CHUNK_SIZE, rawData.length);

                setProgress({
                    current: i + 1,
                    total: chunks.length,
                    percentage,
                    itemsProcessed,
                    totalItems: rawData.length
                });

                setMessage({
                    type: 'info',
                    text: `📤 Enviando lote ${i + 1} de ${chunks.length}...`
                });

                const response = await api.post('/ticket', {
                    data: chunks[i],
                });

                if (response.data.success) {
                    totalSuccess += response.data.results.successCount;
                    totalFailed += response.data.results.failedCount;
                    totalNotFound += response.data.results.notFoundCount;

                    allResults.success.push(...response.data.results.details.success);
                    allResults.failed.push(...response.data.results.details.failed);
                    allResults.notFound.push(...response.data.results.details.notFound);
                }

                await new Promise(resolve => setTimeout(resolve, 50));
            }

            setResults({
                total: rawData.length,
                success: totalSuccess,
                failed: totalFailed,
                notFound: totalNotFound,
                details: allResults
            });

            let finalMessage = `✅ Processamento concluído! ${totalSuccess} atualizações bem-sucedidas`;
            if (totalNotFound > 0) finalMessage += ` | ${totalNotFound} não encontradas`;
            if (totalFailed > 0) finalMessage += ` | ${totalFailed} com erro`;

            setMessage({ type: 'success', text: finalMessage });

            if (uploadRef.current) {
                uploadRef.current.reset();
            }

        } catch (error) {
            console.error("Erro ao enviar dados:", error);
            const errorMsg = error.response?.data?.message || error.message;
            setMessage({ type: 'error', text: `❌ Erro: ${errorMsg}` });
            setErrorMsg(`Erro ao processar: ${errorMsg}`);
        } finally {
            setIsProcessing(false);
            setTimeout(() => setProgress({
                current: 0,
                total: 0,
                percentage: 0,
                itemsProcessed: 0,
                totalItems: 0
            }), 3000);
        }
    }, []);

    const updateSectionData = useCallback((data) => {
        try {
            if (!data || data.length === 0) {
                return;
            }

            setErrorMsg("");
            setMessage({ type: '', text: '' });
            checkListItems(data);

            // Envia automaticamente após validação
            sendUpdatesToBackend(data);

        } catch (err) {
            console.error("Erro ao processar dados:", err);
            setErrorMsg(err.message);
            setMessage({ type: 'error', text: `❌ ${err.message}` });
        }
    }, [sendUpdatesToBackend]);

    const handleCopyAllAWBs = async () => {
        setIsCopying(true);
        setMessage({ type: 'info', text: '📋 Buscando AWBs...' });

        try {
            const response = await api.get('/ticket');

            if (response.data.success && response.data.awbs.length > 0) {
                // Junta todas as AWBs com quebra de linha
                const awbsText = response.data.awbs.join('\n');

                // Copia para o clipboard
                await navigator.clipboard.writeText(awbsText);

                setMessage({
                    type: 'success',
                    text: `✅ ${response.data.total} AWBs copiadas para área de transferência!`
                });
            } else {
                setMessage({
                    type: 'warning',
                    text: '⚠️ Nenhuma AWB encontrada no sistema.'
                });
            }

        } catch (error) {
            console.error('Erro ao copiar AWBs:', error);
            const errorMsg = error.response?.data?.message || error.message;
            setMessage({
                type: 'error',
                text: `❌ Erro ao copiar AWBs: ${errorMsg}`
            });
        } finally {
            setIsCopying(false);
            // Limpa a mensagem após 5 segundos
            setTimeout(() => setMessage({ type: '', text: '' }), 5000);
        }
    };

    return (
        <section id="ticketUpdates">
            <BarMenu />

            <div className="ticket-updates-container">
                <div className="copyAWB">
                    <button
                        className="btn-copy-awbs"
                        onClick={handleCopyAllAWBs}
                        disabled={isCopying}

                    >Copiar AWB dos Tickets</button>
                </div>
                <h1 className="page-title">Atualização de Tickets</h1>
                <p className="page-subtitle">
                    Faça upload do arquivo para atualizar os status automaticamente
                </p>

                {message.text && (
                    <div className={`feedback-message ${message.type}`}>
                        <span>{message.text}</span>
                    </div>
                )}

                {isProcessing && progress.total > 0 && (
                    <div className="progress-container">
                        <div className="progress-header">
                            <div className="progress-title">⏳ Processando Atualizações</div>
                            <div className="progress-percentage">{progress.percentage}%</div>
                        </div>
                        <div className="progress-bar-wrapper">
                            <div className="progress-bar-fill" style={{ width: `${progress.percentage}%` }}>
                                <div className="progress-bar-shine"></div>
                            </div>
                        </div>
                        <div className="progress-details">
                            <div className="progress-detail-item">
                                <strong>Lotes:</strong> {progress.current} / {progress.total}
                            </div>
                            <div className="progress-detail-item">
                                <strong>Registros:</strong> {progress.itemsProcessed} / {progress.totalItems}
                            </div>
                        </div>
                        <div className="progress-warning">
                            ⚠️ <strong>Não feche esta página </strong> até concluir.
                        </div>
                    </div>
                )}

                {results && (
                    <div className="results-container">
                        <div className="results-header">
                            <svg className="success-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <h2>Processamento Concluído</h2>
                        </div>

                        <div className="results-grid">
                            <div className="result-card success">
                                <div className="result-icon">✓</div>
                                <div className="result-content">
                                    <span className="result-label">Sucesso</span>
                                    <span className="result-value">{results.success}</span>
                                </div>
                            </div>

                            <div className="result-card error">
                                <div className="result-icon">✕</div>
                                <div className="result-content">
                                    <span className="result-label">Falhas</span>
                                    <span className="result-value">{results.failed}</span>
                                </div>
                            </div>

                            <div className="result-card warning">
                                <div className="result-icon">!</div>
                                <div className="result-content">
                                    <span className="result-label">Não Encontrados</span>
                                    <span className="result-value">{results.notFound}</span>
                                </div>
                            </div>

                            <div className="result-card total">
                                <div className="result-icon">#</div>
                                <div className="result-content">
                                    <span className="result-label">Total</span>
                                    <span className="result-value">{results.total}</span>
                                </div>
                            </div>
                        </div>

                        {results.details.failed.length > 0 && (
                            <div className="details-section error-details">
                                <h4>
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    Erros Encontrados
                                </h4>
                                <ul>
                                    {results.details.failed.map((item, idx) => (
                                        <li key={idx}>
                                            <span className="awb-code">{item.awb}</span>
                                            <span className="reason">{item.reason}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {results.details.notFound.length > 0 && (
                            <div className="details-section warning-details">
                                <h4>
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    AWBs Não Encontradas
                                </h4>
                                <ul>
                                    {results.details.notFound.map((item, idx) => (
                                        <li key={idx}>
                                            <span className="awb-code">{item.awb}</span>
                                            <span className="reason">{item.reason}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <button
                            className="btn-new-upload"
                            onClick={handleErrorClose}
                        >
                            <MdOutlineFileUpload />
                            Novo Upload
                        </button>
                    </div>
                )}

                {!(results || isProcessing) && (
                    <div className={`upload-section ${isProcessing ? 'uploading' : ''}`}>
                        <div className={`upload-area ${isProcessing ? 'disabled' : ''}`}>
                            <UploadFile
                                ref={uploadRef}
                                setProcessedData={updateSectionData}
                                disabled={isProcessing}
                                headerRow={1}
                                fileType="zip"
                            />
                            {isProcessing && (
                                <div className="upload-overlay">🔒 Aguarde o envio atual...</div>
                            )}
                        </div>

                        <div className="info-card">
                            <h4><span>ℹ️</span> Informações Importantes</h4>
                            <ul>
                                <li><strong>Campos obrigatórios:</strong> AWB, Data do Último Status, Descrição do Status</li>
                                <li>O arquivo é processado <strong>automaticamente</strong> após o upload</li>
                                <li>Lotes de <strong>100 registros</strong> por vez</li>
                                <li><strong>⚠️ Não feche a página durante o processamento</strong></li>
                                <li>AWBs não encontradas no sistema serão listadas no relatório</li>
                            </ul>
                        </div>
                    </div>
                )}
            </div>

            {errorMsg && (
                <ErrorPopup
                    message={errorMsg}
                    onClose={handleErrorClose}
                />
            )}
        </section>
    );
}